import { ApolloLink } from "@apollo/client";
import { isMutationOperation } from "@apollo/client/utilities";
import { throwError } from "rxjs";

import { reportError } from "src/services/errorReporting";

import { OPTION_TWIN_MUTATIONS } from "./optionTwinMutations";
import { OVERLAY_LIVE_ONLY_MUTATIONS } from "./overlayLiveOnlyMutations";
import { OVERLAY_OPTION_ARGUMENT_MUTATIONS } from "./overlayMutationInventory";

/**
 * Mutation operations that may run while a design option is selected: the
 * `*WithOption` twins, the live-only writes that change trunk on purpose, and
 * batch membership mutations that carry the selected `optionId`. Anything else
 * writes the baseline every option is built on. Known gaps are not here: their
 * controls refuse while an option is selected, so one that reaches the network
 * is a broken blocker.
 */
export const OPTION_SAFE_MUTATIONS: ReadonlySet<string> = new Set([
  ...OPTION_TWIN_MUTATIONS,
  ...OVERLAY_LIVE_ONLY_MUTATIONS.flatMap((entry) => entry.operations),
]);

const LIVE_ONLY_MUTATIONS: ReadonlySet<string> = new Set(
  OVERLAY_LIVE_ONLY_MUTATIONS.flatMap((entry) => entry.operations)
);

const OPTION_ARGUMENT_MUTATIONS: ReadonlySet<string> = new Set(
  OVERLAY_OPTION_ARGUMENT_MUTATIONS.map((entry) => entry.operation)
);

type RefusalReason = "baseline" | "otherOption";

const REFUSAL_MESSAGES: Record<RefusalReason, string> = {
  baseline:
    "it would change the baseline while a design option is selected. Switch to Baseline to make this change.",
  otherOption:
    "it targets a different design option from the one selected. Reload the page and try again.",
};

export class OptionWriteRefusedError extends Error {
  readonly operationName: string | undefined;
  readonly optionId: string;
  readonly reason: RefusalReason;

  constructor(
    operationName: string | undefined,
    optionId: string,
    reason: RefusalReason = "baseline"
  ) {
    super(
      `${operationName ?? "An unnamed mutation"} was not sent: ${REFUSAL_MESSAGES[reason]}`
    );
    this.name = "OptionWriteRefusedError";
    this.operationName = operationName;
    this.optionId = optionId;
    this.reason = reason;
  }
}

// Apollo links run outside React, so the option the UI is honouring is
// mirrored here by useOptionWriteGuard.
let guardedOptionId: string | undefined;

export const setOptionWriteGuardOptionId = (
  optionId: string | undefined
): void => {
  guardedOptionId = optionId || undefined;
};

/**
 * Network backstop for the per-control blocks: while a design option is
 * selected, refuses any mutation not in `OPTION_SAFE_MUTATIONS`, and any
 * `*WithOption` twin whose `optionId` is not the selected option, and any
 * option-argument mutation (`mutateMemberships`,
 * `syncForecastPositionDemandMemberships`) whose `optionId` is missing or
 * not the selected option. With no option selected it forwards everything
 * untouched.
 */
export const optionWriteGuardLink = new ApolloLink((operation, forward) => {
  const optionId = guardedOptionId;
  const { operationName } = operation;
  if (!optionId || !isMutationOperation(operation.query)) {
    return forward(operation);
  }
  if (operationName !== undefined && LIVE_ONLY_MUTATIONS.has(operationName)) {
    return forward(operation);
  }
  const isTwin =
    operationName !== undefined && OPTION_TWIN_MUTATIONS.has(operationName);
  const isOptionArgument =
    operationName !== undefined && OPTION_ARGUMENT_MUTATIONS.has(operationName);
  const carriesSelectedOption = operation.variables?.["optionId"] === optionId;
  // A twin or option-argument write is only safe for the option on screen.
  if ((isTwin || isOptionArgument) && carriesSelectedOption) {
    return forward(operation);
  }
  const error = new OptionWriteRefusedError(
    operationName,
    optionId,
    isTwin || (isOptionArgument && operation.variables?.["optionId"])
      ? "otherOption"
      : "baseline"
  );
  console.error(error);
  reportError(error, {
    operation: { operationName },
    optionId,
  });
  return throwError(() => error);
});
